import Phaser from 'phaser';
import { Modal, type ModalConfig } from './Modal';
import { Button } from './Button';
import type { GlobalSignals, SignalSubscription } from '@repo/plugin-signals';

export interface PauseModalConfig extends Omit<ModalConfig, 'title'> {
  title?: string;
  onResume?: () => void;
}

/**
 * Type for scene with signals plugin
 */
interface SceneWithSignals extends Phaser.Scene {
  signals?: { global: GlobalSignals };
}

/**
 * PauseModal - A modal that displays when the game is paused
 *
 * Features:
 * - Automatically shows/hides based on gamePause/gameResume signals
 * - Semi-transparent overlay
 * - Resume button that emits gameResume signal
 * - Customizable appearance via theme
 *
 * Extends the base Modal component for consistent styling.
 */
export class PauseModal extends Modal {
  private resumeButton!: Button;
  private pauseConfig: Required<Pick<PauseModalConfig, 'title' | 'onResume'>> & PauseModalConfig;
  private signals?: GlobalSignals;
  private subscriptions: SignalSubscription[] = [];

  constructor(scene: Phaser.Scene, config: PauseModalConfig) {
    // Set modal config with pause defaults
    super(scene, {
      ...config,
      title: config.title ?? 'PAUSED',
      width: config.width ?? 400,
      height: config.height ?? 300,
    });

    this.pauseConfig = {
      ...config,
      title: config.title ?? 'PAUSED',
      onResume: config.onResume ?? (() => {}),
    };

    this.createPauseContent();
    this.setupSignalListeners();
  }

  private createPauseContent(): void {
    const { height: panelHeight } = this.getPanelDimensions();
    const theme = this.getTheme();

    // Resume button
    this.resumeButton = new Button(this.scene, {
      x: 0,
      y: panelHeight / 2 - 80,
      width: 200,
      height: 60,
      text: 'RESUME',
      theme,
      variant: 'success',
      onClick: () => this.handleResume(),
    });

    // Remove button from scene and add to our container
    this.scene.children.remove(this.resumeButton);
    this.addContent(this.resumeButton);
  }

  private setupSignalListeners(): void {
    // Get signals from the scene's signals plugin
    const signalsPlugin = (this.scene as SceneWithSignals).signals;
    this.signals = signalsPlugin?.global;

    if (!this.signals) {
      console.warn('PauseModal: signals plugin not found on scene');
      return;
    }

    // Listen for pause signal
    this.subscriptions.push(this.signals.lifecycle.gamePause.subscribe(() => this.show()));

    // Listen for resume signal
    this.subscriptions.push(this.signals.lifecycle.gameResume.subscribe(() => this.hide()));
  }

  private handleResume(): void {
    // Emit resume signal
    if (this.signals) {
      this.signals.lifecycle.gameResume.emit({
        timestamp: Date.now(),
        sceneKey: this.scene.scene.key,
      });
    }

    // Call custom callback
    this.pauseConfig.onResume();
  }

  /**
   * Clean up subscriptions and destroy
   */
  public destroy(): void {
    // Unsubscribe from signals
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    this.resumeButton?.destroy();

    super.destroy();
  }
}
